<template>
  <div class="overflow-x-auto flex flex-col space-y-4">
    <div
      ref="container"
      class="scrollbar-none overflow-x-auto flex flex-nowrap shrink-0 space-x-2"
    >
      <button
        v-if="showNextAvailableButton"
        type="button"
        :class="selectedDate ? 'border border-neutral-900  focus:bg-neutral-100' : 'border-2 border-bubblegum-500'"
        class="w-20 h-16 shrink-0 rounded-lg bg-white hover:opacity-70 first:ml-4 sm:first:ml-6"
        @click="nextAvailable"
      >
        <span
          :class="selectedDate ? 'text-neutral-900' : 'text-bubblegum-700'"
          class="block text-xs leading-4 font-bold"
        >
          {{ $t('quote.next-available') }}
        </span>
      </button>
      <button
        v-for="(day, index) in getDays"
        :key="index"
        :class="isActiveDate(day) ? 'border-2 border-bubblegum-500 selected-date' : 'border-neutral-900  focus:bg-neutral-100'"
        class="w-20 h-16 shrink-0 border rounded-lg bg-white hover:opacity-70 first:ml-4 sm:first:ml-6 last:!mr-4 md:last:!mr-6"
        type="button"
        @click="toggleDate(day)"
      >
        <span class="flex flex-col items-center justify-center">
          <span class="text-neutral-500 text-xs">
            {{ buttonDate(day, index) }}
          </span>
          <span
            :class="isActiveDate(day) ? 'text-bubblegum-700 font-bold' : 'text-neutral-900 font-semibold'"
            class="text-xl/5"
          >
            <fa
              v-if="isQuoteLoading && isActiveDate(day) && isToggling"
              :icon="['fas', 'circle-notch']"
              class="text-base text-bubblegum-700"
              spin
            />
            <template v-else>
              {{ day.day }}
            </template>
          </span>
        </span>
      </button>
    </div>
    <div
      class="flex items-center space-x-2 text-neutral-900 font-medium px-standard"
      :class="centerFullDate ? 'justify-center' : 'justify-start'"
    >
      <fa
        :icon="['fal', 'calendar']"
        class="fa-fw text-xl"
      />
      <span class="text-sm">{{ fullDate }}</span>
    </div>
  </div>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCalendar } from '@fortawesome/pro-light-svg-icons';
import { faCircleNotch } from '@fortawesome/pro-solid-svg-icons';
import debounce from 'lodash/debounce';
import { DateTime } from 'luxon';
import { mapGetters } from 'vuex';

import {
  isToday,
  isTomorrow,
} from '@/helpers/dateUtils';

library.add({
  faCalendar,
  faCircleNotch,
});

export default {
  name: 'VetsterDateButton',

  props: {
    centerFullDate: {
      type: Boolean,
      default: false,
    },
    date: {
      type: [Object, String],
      default: undefined,
    },
    showNextAvailableButton: {
      type: Boolean,
      default: false,
    },
    timezone: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isOnFinalPosition: false,
      isOnInitialPosition: false,
      isToggling: false,
      selectedDate: null,
    };
  },

  computed: {
    ...mapGetters('quote', ['isQuoteLoading']),

    fullDate() {
      if (this.selectedDate) {
        return this.selectedDate.toFormat('EEEE, MMMM d, yyyy');
      }

      return this.$t('quote.next-available');
    },
    getDays() {
      const today = DateTime.local().setZone(this.timezone).startOf('day');

      return [...Array(28).keys()]
        .map((day) => today.plus({ day }));
    },
  },

  watch: {
    isQuoteLoading(value) {
      if (!value && this.isToggling) {
        this.isToggling = false;
      }
    },
  },

  created() {
    this.syncSelectedDay(this.date);
  },

  mounted() {
    this.$nextTick(() => {
      this.centerSelectedDate();
      this.updateScrollPositionStatus();
      this.$refs.container.addEventListener('scroll', this.updateScrollPositionStatus);
    });
  },

  beforeDestroy() {
    this.$refs.container.removeEventListener('scroll', this.updateScrollPositionStatus);
  },

  methods: {
    buttonDate(day, index) {
      if (isToday(day)) {
        return this.$t('search.today');
      }

      if (isTomorrow(day)) {
        return this.$t('search.tomorrow');
      }

      if (index > 6) {
        return day.toFormat('LLL');
      }

      return day.toFormat('ccc');
    },
    calculateSelectedDatePosition(container, el) {
      const containerRect = container.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      const elOffsetLeft = elRect.left - containerRect.left + container.scrollLeft;
      const elWidth = el.offsetWidth;
      const containerWidth = container.offsetWidth;

      return elOffsetLeft - (containerWidth / 2) + (elWidth / 2);
    },
    centerSelectedDate() {
      const el = document.querySelector('.selected-date');
      const { container } = this.$refs;

      if (!el || !container) {
        return;
      }

      container.scrollTo({
        left: this.calculateSelectedDatePosition(container, el),
        behavior: 'smooth',
      });
    },
    isActiveDate(day) {
      return DateTime.isDateTime(day) && this.selectedDate && this.selectedDate.hasSame(day, 'day');
    },
    nextAvailable() {
      this.selectedDate = null;
      this.$emit('update', this.selectedDate);
    },
    slideLeft() {
      const { container } = this.$refs;

      container.scrollTo({
        left: container.scrollLeft - container.offsetWidth,
        behavior: 'smooth',
      });
    },
    slideRight() {
      const { container } = this.$refs;

      container.scrollTo({
        left: container.scrollLeft + container.offsetWidth,
        behavior: 'smooth',
      });
    },
    syncSelectedDay(date) {
      if (!date && !this.showNextAvailableButton) {
        [this.selectedDate] = this.getDays;
      }

      if (date) {
        this.selectedDate = DateTime.fromISO(date, {
          zone: this.timezone,
        }).startOf('day');
      }

      this.$nextTick(() => this.$emit('update', this.selectedDate));
    },
    toggleDate(day) {
      this.isToggling = true;
      this.selectedDate = day;
      this.$emit('update', this.selectedDate);
      this.$nextTick(() => this.centerSelectedDate());
    },
    updateScrollPositionStatus: debounce(function emitPosition() {
      const { container } = this.$refs;

      if (!container) {
        return;
      }

      this.isOnInitialPosition = container.scrollLeft === 0;
      this.isOnFinalPosition = container.scrollLeft === container.scrollWidth - container.clientWidth;

      this.$emit('updateControls', {
        isOnInitialPosition: this.isOnInitialPosition,
        isOnFinalPosition: this.isOnFinalPosition,
      });
    }, 25),
  },
};
</script>
